/* eslint react-hooks/exhaustive-deps: "warn" */

import { useInt } from 'hooks/useInt';

import { Faq } from 'ui/atoms/Faq/Faq';
import { Button } from 'ui/atoms/Button/Button';
import { HeroSection } from 'ui/molecules/HeroSection/HeroSection';
import { Section } from 'ui/molecules/Section/Section';
import { RegistryMarquee } from 'ui/molecules/RegistryMarquee/RegistryMarquee';
import { WaterfallEnrichment } from 'ui/molecules/WaterfallEnrichment/WaterfallEnrichment';
import { OurCustomersV2 } from 'ui/molecules/OurCustomers/OurCustomersV2';
import { CtaSection } from 'ui/organisms/CtaSection/CtaSection';
import { DeliveryTable } from 'ui/organisms/DeliveryTable/DeliveryTable';
import { NumberedFeatureCards } from 'ui/organisms/NumberedFeatureCards/NumberedFeatureCards';
import { NumberedRowsSection } from 'ui/organisms/NumberedRowsSection/NumberedRowsSection';
import { PillarCards } from 'ui/organisms/PillarCards/PillarCards';

import { CaseStudyCard } from 'landing-pages/shared/CaseStudyCard/CaseStudyCard';

import styles from './BecomePartnerPage.module.scss';

import {
  getCompanyDataBlocks,
  getContactDataBlocks,
  getDeliveryRows,
  getFaqItems,
  getLicenceGrants,
  getPartnerModels,
  getPipelineRows,
  REGISTRIES,
} from './BecomePartnerPage.data';
import { PartnerFormHubspot } from './PartnerFormHubspot';

const CheckIcon = () => (
  <svg width="11" height="9" viewBox="0 0 11 9" fill="none" aria-hidden="true">
    <path d="M1 4.4 4 7.5 10 1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const BecomePartnerPage = () => {
  const { t } = useInt();

  return (
    <div className={styles.page}>
      <HeroSection
        align="center"
        title={
          <>
            {t('The data layer')} <span className={styles.accent}>{t('under your product.')}</span>
          </>
        }
        description={t(
          'License registry-sourced company data and verified business contact data across 600M+ companies — and pass it on to the people who use your product.',
        )}
        actions={
          <>
            <Button type="blue" size="xxl" href="#apply">
              {t('Apply to partner')}
            </Button>

            <Button type="dark-outline" size="xxl" href="#company-data">
              {t('See every field')}
            </Button>
          </>
        }
      />

      <OurCustomersV2 hasBackground={false} />

      <NumberedFeatureCards
        theme="dark"
        columns={4}
        title={t('Four ways to partner')}
        description={t(
          'The difference between them is who the data reaches and what they may do with it, not what data you get — all four license the same two data sets, set out below. Pick the one that matches what you are building; if you straddle two, we write one agreement that covers both.',
        )}
        items={getPartnerModels(t)}
        actions={
          <Button type="white" size="xl" href="#apply">
            {t('Apply to partner')}
          </Button>
        }
      />

      <NumberedFeatureCards
        theme="light"
        columns={3}
        title={t('What you are allowed to do with it')}
        description={t(
          'This is where most data partnerships die. Incumbent agreements are drafted to keep the data inside your four walls, which is exactly the opposite of what a partner needs. Ours are drafted the other way round.',
        )}
        items={getLicenceGrants(t)}
      />

      <CtaSection
        theme="dark"
        layout="split"
        title={t('Send us the clause that killed the last one')}
        description={t(
          'Legal teams rarely arrive with a general concern. They arrive with one specific paragraph from a previous vendor contract that ended the project: a redistribution ban, a derivative works restriction, a territory cap, a per-record charge that made the unit economics impossible. Send us that paragraph and we will tell you whether we can write around it.',
        )}
        actions={
          <>
            <Button type="blue" size="xl" href="#apply">
              {t('Contact the licensing desk')}
            </Button>

            <Button type="white-outline" size="xl" href="tel:+442045511901">
              +44 20 4551 1901
            </Button>
          </>
        }
      />

      <div id="company-data">
        <NumberedFeatureCards
          theme="light"
          columns={4}
          title={t('What is in the company data')}
          description={t(
            'Whichever model you pick above, these are the blocks you license. First-party, sourced directly from official government registries across 600M+ companies in 200+ countries — eight blocks, together or separately.',
          )}
          items={getCompanyDataBlocks(t)}
          actions={
            <div className={styles.sourceLine}>
              <p>
                {t(
                  'Collected directly from 400+ official government registries and gazettes across 200+ countries — no intermediary data vendor in between.',
                )}
              </p>

              <RegistryMarquee items={REGISTRIES} moreLabel={t('+ 380 more')} />
            </div>
          }
        />
      </div>

      <PillarCards
        theme="dark"
        title={t('What is in the business contact data')}
        description={t(
          'The second data set, licensed under the same agreement. Business contacts only, resolvable by person, company domain or LinkedIn profile and tied back to the company record — sourced separately from the registry data and labelled as such in the API.',
        )}
        items={getContactDataBlocks(t)}
      />

      <Section className={styles.waterfallSection}>
        <WaterfallEnrichment
          label={t('Waterfall enrichment')}
          lede={
            <>
              {t('We connect to')} <b>{t('15 contact data partners')}</b>{' '}
              {t(
                'rather than relying on one. Every lookup is routed through them in sequence, which is what lifts the',
              )}{' '}
              <b>{t('match rate')}</b> {t('— no single vendor covers every market — and each field is taken from the')}{' '}
              <b>{t('first partner that verifies it')}</b>,{' '}
              {t(
                'so quality holds at volume. The run stops the moment the record is complete, so nothing is queried twice and the cost stays down.',
              )}
            </>
          }
        />
      </Section>

      <DeliveryTable
        title={t('How the data reaches your stack')}
        description={t(
          'Seven ways to take the same data. Pick one or combine them — a live API for the paths inside your product, a bulk feed for your warehouse, or a prebuilt connector for the tools your users already open. Fields are mapped to your names, types and identifiers before the first delivery.',
        )}
        rows={getDeliveryRows(t)}
      />

      <NumberedRowsSection
        heading={t('The pipeline is the product you were about to build')}
        description={t(
          'Teams who try this in-house usually get three registries working, then discover that the fourth changes its export format without notice and the fifth charges per document. Here is what sits behind the licence instead.',
        )}
        rows={getPipelineRows(t)}
        actions={
          <>
            <Button type="blue" size="xl" href="#apply">
              {t('Request a demo')}
            </Button>

            <Button type="dark-outline" size="xl" href="https://www.globaldatabase.com/our-data">
              {t('How we collect our data')}
            </Button>
          </>
        }
      />

      <Section
        align="center"
        title={t('Already inside products your buyers know')}
        description={t(
          'The same records reach end users through compliance, screening, risk and enrichment platforms as well as through our own application.',
        )}
      >
        <CaseStudyCard
          title={t('Authoritative and traceable — a registry-sourced record we can stand behind')}
          summary={t(
            "When you're resolving entities and mapping risk across borders, the foundation has to be authoritative and traceable. Global Database gives us that across the markets we're expanding into, and their team absorbs the registry-level differences and constant changes, so ours stays focused on the intelligence layer rather than the data plumbing.",
          )}
          company="Adam Mulliken, Quantifind"
          href="/customers"
        />
      </Section>

      <Section
        id="apply"
        theme="dark"
        layout="split"
        title={t('Apply to partner')}
        description={t(
          'Tell us what you are building. A named person on the partnerships team replies within two business days, in writing.',
        )}
        actions={
          <ul className={styles.applyList}>
            {[
              t('No fee to apply and no minimum commitment to start a conversation.'),
              t('Integration and embedded partners receive a scoped sample in their own schema before signing.'),
              t('Referral partners receive fee terms in writing before the first introduction.'),
              t(
                'Every delivery is logged, encrypted in transit and processed under a signed data processing agreement.',
              ),
            ].map((item) => (
              <li key={item}>
                <span className={styles.applyCheck} aria-hidden="true">
                  <CheckIcon />
                </span>
                {item}
              </li>
            ))}
          </ul>
        }
      >
        <PartnerFormHubspot />
      </Section>

      <Faq className={styles.faq} title={t('Questions partners ask first')} data={getFaqItems(t)} />
    </div>
  );
};

// eslint-disable-next-line import/no-default-export
export default BecomePartnerPage;
